import { createRouter, createWebHistory, type RouteLocationNormalizedGeneric } from 'vue-router';
import routes from './routes';
import { useAuthStore } from '@/stores/auth';
import posthog from 'posthog-js';
import { nextTick } from 'vue';
import { usePermissionsStore } from '@/stores/permissions.store';
import { watch } from 'vue';
import type { UserRole } from '@/types/auth';
import i18n, { ensureI18nModuleLoaded, currentLanguage } from '@/i18n';
import type { I18nModuleName } from '@/i18n';
import { PARTICIPANT_LANDING_ROUTES, ADMIN_LANDING_ROUTES } from '@/router/landing-routes';
import { PARTICIPANT_NAV_ROUTES, ADMIN_NAV_ROUTES } from '@/config/sidebar/nav-route-registry';

const BASE_TITLE = import.meta.env.VITE_SITE_TITLE;

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    linkActiveClass: 'active',
    linkExactActiveClass: 'exact-active',
});

// Global navigation guard
// - Only authenticated users can access simulation area
// - Only authenticated instructors and admins can access admin area
// - If the route is specified to be admin only, disallow direct access to the route
router.beforeEach(async (to) => {
    // Signup only makes sense as part of accepting an organization invite.
    if (to.name === 'signUp' && !to.query.organizationInviteId) {
        return { name: 'login' };
    }

    if (to.meta.noAuth) {
        await loadTranslations(to);
        return true;
    }

    const permissionsStore = usePermissionsStore();
    const { fetchUserIfNecessary, organizationId, role } = useAuthStore();

    // Wait for user to be loaded
    if (fetchUserIfNecessary) {
        await fetchUserIfNecessary();
    }

    if (!organizationId.value || !role.value) {
        // Store the intended destination before redirecting to login
        const redirectTo = encodeURIComponent(to.fullPath);
        return {
            name: 'login',
            query: { redirect: redirectTo },
        };
    }

    // '/app' and '/admin' resolve to a landing page via a static `redirect` in routes.ts, which
    // runs synchronously during route matching — before organization metadata (an async fetch)
    // is necessarily loaded. Once it's loaded, correct course here if the org has configured a
    // different default landing route than the static fallback the route matcher just picked.
    const cameFromPortalRoot = to.redirectedFrom?.path === '/app' || to.redirectedFrom?.path === '/admin';
    if (cameFromPortalRoot) {
        const { currentOrganization } = useAuthStore();
        if (!currentOrganization.value) {
            await new Promise<void>((resolve) => {
                const stop = watch(currentOrganization, (org) => {
                    if (org) {
                        stop();
                        resolve();
                    }
                });
            });
        }

        const isAdminPortal = to.redirectedFrom?.path === '/admin';
        const configuredRoute = isAdminPortal
            ? currentOrganization.value?.metadata?.navigation?.defaultAdminRoute
            : currentOrganization.value?.metadata?.navigation?.defaultParticipantRoute;
        const allowedRoutes = isAdminPortal ? ADMIN_LANDING_ROUTES : PARTICIPANT_LANDING_ROUTES;

        if (configuredRoute && allowedRoutes.some((r) => r.name === configuredRoute) && to.name !== configuredRoute) {
            return { name: configuredRoute };
        }
    }

    // Check for role requirements
    const requiredRoles = to.matched.map((r) => r.meta.requiresRole).filter(Boolean);
    if (requiredRoles.length > 0 && role.value) {
        // Check if user's role is allowed in any of the required role arrays
        const userRole = role.value as UserRole;
        const hasRequiredRole = requiredRoles.some((roleArray) => roleArray && roleArray.includes(userRole));

        if (!hasRequiredRole) {
            console.warn('Access denied: user role', role.value, 'not in required roles:', requiredRoles);
            return { path: '/not-authorized' };
        }
    }

    // Block direct/typed navigation to a sidenav route the org's allow-list excludes — mirrors
    // the hiding done in NavItemsRenderer (via useNavVisibility.isRouteAllowed) so the
    // restriction can't be bypassed by URL. Unset/empty allow-list = no restriction.
    const isAdminNavRoute = typeof to.name === 'string' && ADMIN_NAV_ROUTES.some((r) => r.name === to.name);
    const isParticipantNavRoute = typeof to.name === 'string' && PARTICIPANT_NAV_ROUTES.some((r) => r.name === to.name);
    if (isAdminNavRoute || isParticipantNavRoute) {
        const { currentOrganization } = useAuthStore();
        // A typed/bookmarked URL is a fresh page load — organization metadata (an async fetch)
        // may not have loaded yet. Wait for it so the check below isn't done against stale
        // (undefined) data, which would fail open and let the restricted route through.
        if (!currentOrganization.value) {
            await new Promise<void>((resolve) => {
                const stop = watch(currentOrganization, (org) => {
                    if (org) {
                        stop();
                        resolve();
                    }
                });
            });
        }

        const allowedRoutes = isAdminNavRoute
            ? currentOrganization.value?.metadata?.navigation?.allowedAdminRoutes
            : currentOrganization.value?.metadata?.navigation?.allowedParticipantRoutes;

        if (
            allowedRoutes &&
            allowedRoutes.length > 0 &&
            typeof to.name === 'string' &&
            !allowedRoutes.includes(to.name)
        ) {
            return { path: '/not-authorized' };
        }
    }

    // Block account settings entirely for orgs that disable self-service editing.
    const requiresAccountSettingsEnabled = to.matched.some((r) => r.meta.requiresAccountSettingsEnabled);
    if (requiresAccountSettingsEnabled) {
        const { currentOrganization } = useAuthStore();
        if (!currentOrganization.value) {
            await new Promise<void>((resolve) => {
                const stop = watch(currentOrganization, (org) => {
                    if (org) {
                        stop();
                        resolve();
                    }
                });
            });
        }

        if (currentOrganization.value?.metadata?.disableAccountSettings) {
            return { path: '/not-authorized' };
        }
    }

    // Gather all required permissions from matched routes
    const requiredPermissions = to.matched.map((r) => r.meta.requiredPermission).filter(Boolean);
    // Only run permission logic if requiredPermissions is not empty
    if (requiredPermissions.length > 0) {
        if (permissionsStore.permissions.value.length === 0) {
            await new Promise((resolve) => {
                const stop = watch(
                    () => permissionsStore.permissions.value.length,
                    (len) => {
                        if (len > 0) {
                            stop();
                            resolve(true);
                        }
                    },
                );
            });
        }

        // Check all required permissions
        for (const perm of requiredPermissions) {
            if (typeof perm === 'string' && !permissionsStore.hasPermission(perm)) {
                console.warn('Missing required permission:', perm);
                return { path: '/not-authorized' };
            }
            if (Array.isArray(perm) && !permissionsStore.hasPermission(perm)) {
                console.warn('Missing one of required permissions:', perm);
                return { path: '/not-authorized' };
            }
        }
    }

    // Block access when the organization has not enabled the AI assistant feature.
    const requiresAIAssistant = to.matched.some((r) => r.meta.requiresAIAssistant);
    if (requiresAIAssistant) {
        const { currentOrganization } = useAuthStore();
        // A typed/bookmarked URL is a fresh page load — organization metadata (an async fetch)
        // may not have loaded yet. Wait for it so the check below isn't done against stale
        // (undefined) data, which would fail open and let the restricted route through.
        if (!currentOrganization.value) {
            await new Promise<void>((resolve) => {
                const stop = watch(currentOrganization, (org) => {
                    if (org) {
                        stop();
                        resolve();
                    }
                });
            });
        }

        if (!currentOrganization.value?.metadata?.enableAIAssistant) {
            return { path: '/not-authorized' };
        }
    }

    // Check for super admin requirement
    const requiresSuperAdmin = to.matched.some((r) => r.meta.requiresSuperAdmin);
    if (requiresSuperAdmin) {
        const { user, possibleOrganizationIds } = useAuthStore();
        const defaultOrganizationId = import.meta.env.VITE_DEFAULT_ORGANIZATION_ID;

        // Check if user has admin role in the default organization
        // User can access super admin routes from any organization they're part of,
        // as long as they have admin role in the default organization
        const userOrganizations = user.value?.app_metadata?.organizations || {};
        const hasDefaultOrgAccess = possibleOrganizationIds.value.includes(defaultOrganizationId);
        const isAdminInDefaultOrg = userOrganizations[defaultOrganizationId]?.includes('admin');

        if (!hasDefaultOrgAccess || !isAdminInDefaultOrg) {
            console.warn('Super admin access required: user must have admin role in default organization');

            return { path: '/not-authorized' };
        }
    }

    // Block write routes when the org has a pool but the user's license is not active
    const requiresActiveLicense = to.matched.some((r) => r.meta.requiresActiveLicense);
    if (requiresActiveLicense) {
        const { orgHasPool, isLicensed } = useAuthStore();
        // orgHasPool is populated by an async watcher that fires after user/orgId are set.
        // On first navigation it may still be undefined (watcher pending). Wait for it.
        if (orgHasPool.value === undefined) {
            await new Promise<void>((resolve) => {
                const stop = watch(orgHasPool, (val) => {
                    if (val !== undefined) {
                        stop();
                        resolve();
                    }
                });
            });
        }
        if (orgHasPool.value === true && !isLicensed.value) {
            return { name: 'coursesManagement' };
        }
    }

    await loadTranslations(to);

    // If no required permissions or super admin, just allow navigation
    return true;
});

/**
 * Parses a supabase query string into an object. The hash query string starts with # but is otherwise built like regular query strings
 * @param hashQueryString
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseQuery(hashQueryString: string): Record<string, string> {
    const query: Record<string, string> = {};
    const pairs = hashQueryString.substring(1).replace(/\+/g, '%20').split('&');
    for (const pair of pairs) {
        const [key, value] = pair.split('=');
        query[decodeURIComponent(key)] = decodeURIComponent(value ?? '');
    }
    return query;
}

async function loadTranslations(to: RouteLocationNormalizedGeneric) {
    // Load all i18n modules declared across the matched route hierarchy in parallel
    const i18nModules = to.matched.map((r) => r.meta.i18nModule).filter((m): m is I18nModuleName => Boolean(m));

    if (i18nModules.length > 0) {
        try {
            await Promise.all(i18nModules.map((m) => ensureI18nModuleLoaded(currentLanguage.value, m)));
        } catch {
            // Module load failure — English fallback is used; navigation continues
        }
    }
}

router.afterEach((to, from) => {
    // Some views (e.g. ContentEditorView) track their current selection via useRouteQuery, which
    // issues its own router.replace() on nextTick whenever that query param changes — re-running
    // this guard for the *same* route. Only reset to the static fallback when actually entering a
    // different route; otherwise this would stomp on a dynamic title usePageTitle() just set (e.g.
    // the real activity/course name) back to the generic titleKey fallback.
    if (to.name !== from.name) {
        document.title = to.meta.titleKey ? `${i18n.global.t(to.meta.titleKey)} · ${BASE_TITLE}` : BASE_TITLE;
    }

    // Capture pageviews with PostHog
    if (posthog.__loaded) {
        nextTick(() => {
            posthog.capture('$pageview', {
                $current_url: to.fullPath,
            });
        });
    }
});

export default router;
